import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';
import { useAuth } from '@/hooks/useAuth';
import type { CoachingEngagement } from '@/types/coaching';

interface CoachingEngagementWithCoach extends CoachingEngagement {
  coach_display_name?: string | null;
}

export function useCoachingEngagement() {
  const { user } = useAuth();
  const [engagements, setEngagements] = useState<CoachingEngagementWithCoach[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEngagements = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Fetch engagements where user is the client
      const { data, error: fetchError } = await supabase
        .from('coaching_engagements')
        .select('*')
        .eq('client_user_id', user.id)
        .in('status', ['active', 'paused', 'grace_period']);

      if (fetchError) throw fetchError;

      // Enrich with coach names
      const enriched: CoachingEngagementWithCoach[] = [];
      for (const eng of (data || []) as unknown as CoachingEngagement[]) {
        const { data: coachProfile } = await supabase
          .from('coach_profiles')
          .select('display_name')
          .eq('user_id', eng.coach_user_id)
          .maybeSingle();

        enriched.push({
          ...eng,
          coach_display_name: coachProfile?.display_name ?? null,
        });
      }

      setEngagements(enriched);
    } catch (err) {
      reportError(err, 'settings', { action: 'fetch-coaching-engagements' });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchEngagements();
  }, [fetchEngagements]);

  const acceptInvite = async (token: string): Promise<{ success: boolean; coachName?: string; error?: string }> => {
    try {
      const { data, error: fnError } = await supabase.functions.invoke('accept-coaching-engagement', {
        body: { token },
      });

      if (fnError) throw fnError;
      if (!data?.success) {
        return { success: false, error: data?.error || 'Unknown error' };
      }

      await fetchEngagements();
      return { success: true, coachName: data.coach_name };
    } catch (err) {
      reportError(err, 'auth', { action: 'accept-coaching-invite' });
      return { success: false, error: err instanceof Error ? err.message : 'Failed to accept' };
    }
  };

  const activeCoaches = engagements.filter((e) => e.status === 'active');

  return {
    engagements,
    activeCoaches,
    loading,
    acceptInvite,
    refetch: fetchEngagements,
  };
}
